import React, { useEffect, useState } from 'react';
import { Company, EmailDraft, Project } from '../../types';

type EmailTone = 'professional' | 'casual';

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

const getAutoBodyFontSize = (body: string) => {
  const textLength = (body || '').trim().length;

  if (textLength > 2600) return 8.8;
  if (textLength > 2200) return 9.2;
  if (textLength > 1800) return 9.7;
  if (textLength > 1450) return 10.2;
  if (textLength > 1100) return 10.7;
  return 11.2;
};

interface EmailComposerModalProps {
  isOpen: boolean;
  project: Project | null;
  company: Company | null;
  draft: EmailDraft | null;
  isGenerating: boolean;
  isSending: boolean;
  onClose: () => void;
  onRerun: (tone: EmailTone) => void;
  onSend: (draft: EmailDraft) => Promise<void> | void;
}

export const EmailComposerModal: React.FC<EmailComposerModalProps> = ({
  isOpen,
  project,
  company,
  draft,
  isGenerating,
  isSending,
  onClose,
  onRerun,
  onSend,
}) => {
  const [editableDraft, setEditableDraft] = useState<EmailDraft | null>(draft);
  const [isPrinting, setIsPrinting] = useState(false);
  const [photoPosition, setPhotoPosition] = useState({ x: 50, y: 50 });
  const [bodyFontSize, setBodyFontSize] = useState(11.2);

  useEffect(() => {
    setEditableDraft(draft);
    setPhotoPosition({ x: 50, y: 50 });
    setBodyFontSize(11.2);
  }, [draft]);

  if (!isOpen || !company || !editableDraft) return null;

  const activeTone = editableDraft.meta?.tone === 'casual' ? 'casual' : 'professional';
  const senderContactLines = (editableDraft.contactBlock || '')
    .split(/\n/)
    .map((line) => line.trim())
    .filter(Boolean);
  const photoObjectPosition = `${photoPosition.x}% ${photoPosition.y}%`;
  const effectiveBodyFontSize = Math.min(bodyFontSize, getAutoBodyFontSize(editableDraft.body || ''));

  const handleFieldChange = (field: keyof EmailDraft, value: string) => {
    setEditableDraft((current) => {
      if (!current) return current;
      return { ...current, [field]: value };
    });
  };

  const handlePrint = () => {
    setIsPrinting(true);
    const previousTitle = document.title;
    document.title = `${company.name} - Email Draft`;

    window.setTimeout(() => {
      window.print();
      window.setTimeout(() => {
        document.title = previousTitle;
        setIsPrinting(false);
      }, 400);
    }, 120);
  };

  return (
    <>
      {isPrinting && (
        <div className="fixed inset-0 z-[150] bg-white text-slate-900 p-8 print:p-0">
          <style>{`
            @media print {
              @page {
                size: A4;
                margin: 0;
              }
              body {
                background: white !important;
              }
              .bdat-email-print {
                width: 210mm;
                min-height: 297mm;
                max-width: 210mm;
                margin: 0;
                box-shadow: none;
              }
              .bdat-email-print-body {
                white-space: pre-wrap;
              }
            }
          `}</style>
          <div
            className="bdat-email-print mx-auto overflow-hidden border border-slate-200 bg-white shadow-sm"
            style={{ width: '210mm', minHeight: '297mm' }}
          >
            <div className="flex min-h-[297mm] flex-col px-[14mm] py-[14mm]">
              <div className="flex items-start justify-between gap-6 border-b border-slate-200 pb-5">
                <div className="min-w-0 flex-1">
                  <h1 className="text-[20pt] font-bold leading-tight">{company.name}</h1>
                </div>
                <div className="h-[20mm] w-[20mm] shrink-0 overflow-hidden rounded-[3mm] border border-slate-300 bg-slate-100">
                  {project?.clientPhoto?.data ? (
                    <img
                      src={project.clientPhoto.data as string}
                      alt="Header"
                      className="h-full w-full object-cover"
                      style={{ objectPosition: photoObjectPosition }}
                    />
                  ) : (
                    <div className="h-full w-full bg-slate-100" />
                  )}
                </div>
              </div>

              <div className="mt-5 grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)] gap-3 text-[10.5pt]">
                <div>
                  <div className="text-[9px] font-bold uppercase tracking-[0.2em] text-slate-500">Van</div>
                  <div className="mt-1 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2">{editableDraft.from || 'Geen afzender ingesteld'}</div>
                </div>
                <div>
                  <div className="text-[9px] font-bold uppercase tracking-[0.2em] text-slate-500">Aan</div>
                  <div className="mt-1 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2">{editableDraft.to || 'Geen ontvanger ingevuld'}</div>
                </div>
              </div>

              <div className="mt-3">
                <div className="text-[9px] font-bold uppercase tracking-[0.2em] text-slate-500">Onderwerp</div>
                <div className="mt-1 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2">{editableDraft.subject || 'Geen onderwerp ingevuld'}</div>
              </div>

              {senderContactLines.length > 0 && (
                <div className="mt-3">
                  <div className="text-[9px] font-bold uppercase tracking-[0.2em] text-slate-500">Afzendergegevens</div>
                  <div className="mt-1 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 whitespace-pre-wrap leading-5">
                    {senderContactLines.join('\n')}
                  </div>
                </div>
              )}

              <div className="mt-5 flex-1">
                <div className="text-[9px] font-bold uppercase tracking-[0.2em] text-slate-500">Bericht</div>
                <div
                  className="bdat-email-print-body mt-2 h-full rounded-2xl border border-slate-200 bg-white px-4 py-4 shadow-sm"
                  style={{
                    fontSize: `${effectiveBodyFontSize}pt`,
                    lineHeight: 1.55,
                    overflowWrap: 'anywhere',
                  }}
                >
                  {editableDraft.body || 'Geen inhoud ingevuld'}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="fixed inset-0 z-[140] flex items-stretch justify-center overflow-y-auto bg-black/80 p-3 backdrop-blur-md print:hidden sm:items-center sm:p-4">
        <div className="my-auto flex h-[min(940px,calc(100vh-1.5rem))] max-h-[calc(100vh-1.5rem)] w-full max-w-[min(1280px,calc(100vw-1.5rem))] min-h-0 flex-col overflow-hidden rounded-[2rem] border border-white/10 bg-[#0d1423] text-white shadow-2xl sm:h-[min(940px,calc(100vh-2rem))] sm:max-h-[calc(100vh-2rem)] sm:max-w-[min(1280px,calc(100vw-2rem))]">
          <div className="flex items-center justify-between border-b border-white/10 px-6 py-5">
            <div>
              <div className="text-[10px] font-black uppercase tracking-[0.3em] text-slate-500">Mail AI</div>
              <h2 className="mt-2 text-2xl font-bold tracking-tight">E-mail voor {company.name}</h2>
            </div>
            <button
              onClick={onClose}
              className="rounded-xl border border-white/10 px-4 py-2 text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 transition-colors hover:text-white"
            >
              Sluiten
            </button>
          </div>

          <div className="grid min-h-0 flex-1 grid-cols-1 overflow-hidden lg:grid-cols-[300px_minmax(0,1fr)]">
            <aside className="min-h-0 overflow-y-auto border-b border-white/10 bg-black/20 p-6 lg:border-b-0 lg:border-r">
              <div className="space-y-6">
                <div>
                  <div className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-500">Rerun Toon</div>
                  <div className="mt-3 grid grid-cols-2 gap-3">
                    <button
                      onClick={() => onRerun('casual')}
                      disabled={isGenerating || isSending}
                      className={`rounded-2xl border px-4 py-4 text-[10px] font-black uppercase tracking-[0.2em] transition-all ${
                        activeTone === 'casual'
                          ? 'border-emerald-400/60 bg-emerald-500/15 text-emerald-200'
                          : 'border-white/10 bg-white/5 text-slate-300 hover:bg-white/10'
                      } disabled:opacity-50`}
                    >
                      Casual
                    </button>
                    <button
                      onClick={() => onRerun('professional')}
                      disabled={isGenerating || isSending}
                      className={`rounded-2xl border px-4 py-4 text-[10px] font-black uppercase tracking-[0.2em] transition-all ${
                        activeTone === 'professional'
                          ? 'border-indigo-400/60 bg-indigo-500/15 text-indigo-200'
                          : 'border-white/10 bg-white/5 text-slate-300 hover:bg-white/10'
                      } disabled:opacity-50`}
                    >
                      Proff
                    </button>
                  </div>
                  <p className="mt-3 text-[11px] leading-5 text-slate-400">
                    Genereer dezelfde mail opnieuw in een losse stijl voordat je hem verzendt.
                  </p>
                </div>

                <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
                  <div className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-500">Acties</div>
                  <div className="mt-4 space-y-3">
                    <button
                      onClick={handlePrint}
                      disabled={isGenerating}
                      className="w-full rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-[10px] font-black uppercase tracking-[0.2em] text-slate-200 transition-colors hover:bg-white/10 disabled:opacity-50"
                    >
                      Print / PDF
                    </button>
                    <button
                      onClick={() => onSend(editableDraft)}
                      disabled={isGenerating || isSending}
                      className="w-full rounded-xl bg-[#5249f3] px-4 py-3 text-[10px] font-black uppercase tracking-[0.2em] text-white transition-colors hover:bg-[#6359f5] disabled:opacity-50"
                    >
                      {isSending ? 'Versturen...' : 'Verzend Mail'}
                    </button>
                  </div>
                </div>

                <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
                  <div className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-500">Layout</div>
                  <div className="mt-4 space-y-4">
                    <div>
                      <div className="flex items-center justify-between text-[10px] font-bold uppercase tracking-[0.18em] text-slate-400">
                        <span>Lettergrootte</span>
                        <span>{effectiveBodyFontSize.toFixed(1)} pt</span>
                      </div>
                      <input
                        type="range"
                        min="8.8"
                        max="11.2"
                        step="0.1"
                        value={bodyFontSize}
                        onChange={(event) => setBodyFontSize(clamp(Number(event.target.value), 8.8, 11.2))}
                        className="mt-3 w-full accent-indigo-400"
                      />
                      <p className="mt-2 text-[11px] leading-5 text-slate-400">
                        De printversie blijft binnen A4 en verlaagt de lettergrootte automatisch als de tekst te lang wordt.
                      </p>
                    </div>

                    {project?.clientPhoto?.data && (
                      <div className="border-t border-white/10 pt-4">
                        <div className="flex items-start justify-between gap-4">
                          <div>
                            <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-400">Fotohouder</div>
                            <p className="mt-2 text-[11px] leading-5 text-slate-400">
                              Grotere foto rechts in een vaste placeholder van 20mm x 20mm.
                            </p>
                          </div>
                          <div className="h-[20mm] w-[20mm] shrink-0 overflow-hidden rounded-[3mm] border border-white/10 bg-black/30">
                            <img
                              src={project.clientPhoto.data as string}
                              alt="Header"
                              className="h-full w-full object-cover"
                              style={{ objectPosition: photoObjectPosition }}
                            />
                          </div>
                        </div>
                        <div className="mt-4 space-y-3">
                          <div>
                            <div className="flex items-center justify-between text-[10px] font-bold uppercase tracking-[0.18em] text-slate-400">
                              <span>Foto X</span>
                              <span>{photoPosition.x}%</span>
                            </div>
                            <input
                              type="range"
                              min="0"
                              max="100"
                              step="1"
                              value={photoPosition.x}
                              onChange={(event) => setPhotoPosition((current) => ({ ...current, x: Number(event.target.value) }))}
                              className="mt-2 w-full accent-indigo-400"
                            />
                          </div>
                          <div>
                            <div className="flex items-center justify-between text-[10px] font-bold uppercase tracking-[0.18em] text-slate-400">
                              <span>Foto Y</span>
                              <span>{photoPosition.y}%</span>
                            </div>
                            <input
                              type="range"
                              min="0"
                              max="100"
                              step="1"
                              value={photoPosition.y}
                              onChange={(event) => setPhotoPosition((current) => ({ ...current, y: Number(event.target.value) }))}
                              className="mt-2 w-full accent-indigo-400"
                            />
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div className="rounded-2xl border border-white/10 bg-white/5 p-4 text-[11px] leading-5 text-slate-400">
                  De tekst hieronder is volledig bewerkbaar. Wat je hier aanpast wordt ook gebruikt voor printen en verzenden.
                </div>
              </div>
            </aside>

            <div className="min-h-0 overflow-y-auto p-6">
              <div className="space-y-5">
                <div className="rounded-[1.6rem] border border-white/10 bg-black/20 px-5 py-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0">
                      <div className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-500">Briefkop</div>
                      <div className="mt-2 text-lg font-bold text-white">{company.name}</div>
                      <div className="mt-1 text-xs text-slate-400">A4-layout met compacte fotohouder rechts.</div>
                    </div>
                    <div className="h-[20mm] w-[20mm] shrink-0 overflow-hidden rounded-[3mm] border border-white/10 bg-black/30">
                      {project?.clientPhoto?.data ? (
                        <img
                          src={project.clientPhoto.data as string}
                          alt="Header"
                          className="h-full w-full object-cover"
                          style={{ objectPosition: photoObjectPosition }}
                        />
                      ) : (
                        <div className="h-full w-full bg-black/30" />
                      )}
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
                  <div>
                    <label className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-500">Van</label>
                    <input
                      value={editableDraft.from || ''}
                      onChange={(event) => handleFieldChange('from', event.target.value)}
                      className="mt-2 w-full rounded-2xl border border-white/10 bg-black/20 px-4 py-3 text-sm text-white outline-none transition-all focus:border-indigo-400/50 focus:ring-1 focus:ring-indigo-500"
                    />
                  </div>
                  <div>
                    <label className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-500">Aan</label>
                    <input
                      value={editableDraft.to}
                      onChange={(event) => handleFieldChange('to', event.target.value)}
                      className="mt-2 w-full rounded-2xl border border-white/10 bg-black/20 px-4 py-3 text-sm text-white outline-none transition-all focus:border-indigo-400/50 focus:ring-1 focus:ring-indigo-500"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-4 xl:grid-cols-[minmax(0,1fr)_220px]">
                  <div>
                    <label className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-500">Onderwerp</label>
                    <input
                      value={editableDraft.subject}
                      onChange={(event) => handleFieldChange('subject', event.target.value)}
                      className="mt-2 w-full rounded-2xl border border-white/10 bg-black/20 px-4 py-3 text-sm text-white outline-none transition-all focus:border-indigo-400/50 focus:ring-1 focus:ring-indigo-500"
                    />
                  </div>
                  <div>
                    <label className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-500">Status</label>
                    <div className="mt-2 rounded-2xl border border-white/10 bg-black/20 px-4 py-3 text-sm text-slate-300">
                      {isGenerating ? 'Nieuwe versie genereren...' : isSending ? 'Mail verwerken...' : 'Concept gereed'}
                    </div>
                  </div>
                </div>

                <div>
                  <label className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-500">Afzendergegevens</label>
                  <textarea
                    value={editableDraft.contactBlock || ''}
                    onChange={(event) => handleFieldChange('contactBlock', event.target.value)}
                    className="mt-2 min-h-[120px] w-full rounded-[1.5rem] border border-white/10 bg-black/20 px-5 py-5 text-sm leading-7 text-white outline-none transition-all focus:border-indigo-400/50 focus:ring-1 focus:ring-indigo-500"
                  />
                </div>

                <div>
                  <label className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-500">Bericht</label>
                  <textarea
                    value={editableDraft.body}
                    onChange={(event) => handleFieldChange('body', event.target.value)}
                    className="mt-2 min-h-[420px] w-full rounded-[1.5rem] border border-white/10 bg-black/20 px-5 py-5 text-sm leading-7 text-white outline-none transition-all focus:border-indigo-400/50 focus:ring-1 focus:ring-indigo-500"
                    style={{
                      fontSize: `${effectiveBodyFontSize}pt`,
                      lineHeight: 1.65,
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
